<script>
  import AudioPlayer from "$app/AudioPlayer.svelte";
  import Structure from "$app/Structure.svelte";
  import { getAPI } from "$lib/api.js";
  import { onMount } from "svelte";
  import { toast } from "$lib/utils/toast.js";
  import LoaderCircle from "@lucide/svelte/icons/loader-circle";
  import init, {
    get_song_structure,
  } from "../../../pkg/song_format.js";
  import { initWasm } from "$lib/extensions/lib/wasmInit.js";
  import {
    enrichSongStructure,
    generateNotesGrouped,
    formatSeconds,
    instrumentColors,
    buildSyntaxHighlightedHTML,
    EMPTY_SONG_STRUCTURE,
    parseSongStructureSafe,
    buildStaticDecorationsUtil,
  } from "$lib/extensions/lib/songUtils.js";
  import { createNoteHighlighter } from "$lib/extensions/lib/noteHighlighting.svelte.js";

  const { id = null, songFile: initialSongFile = null, mode = "tiny" } = $props();

  let file = $state(null);
  let songFile = $state("");
  let songStructure = $state(null);
  let loading = $state(true);
  let isGenerating = $state(false);
  let contentDiv = $state(null);
  let currentTime = $state(0);
  let playing = $state(false);
  let audioPlayer = $state(null);

  // Note highlighting - managed directly in RAF loop, not through effects
  const noteElements = new Map();
  let notesGrouped = $state(null);
  const highlighter = createNoteHighlighter();

  onMount(async () => {
    await initWasm();
    await loadFile();
  });

  async function loadFile() {
    loading = true;
    if (initialSongFile) {
      // Use provided song file directly
      songFile = initialSongFile;
      processSong();
      loading = false;
    } else if (id) {
      // Fetch file from API
      const response = await getAPI(`/public/file/${id}`, false);
      if (response.Error) {
        console.error(`Error loading file: ${response.Error.message}`);
        loading = false;
        return;
      }
      file = response.Success;
      songFile = file?.file?.contents || "";
      processSong();
      loading = false;
    } else {
      console.error("Either id or songFile must be provided");
      loading = false;
    }
  }

  function processSong() {
    if (contentDiv) {
      contentDiv.innerHTML = "";
    }
    noteElements.clear();

    const result = parseSongStructureSafe(get_song_structure, songFile);
    songStructure = result.structure;
    notesGrouped = result.notesGrouped;
  }

  // Build decorations when song structure changes - only runs when notesGrouped changes
  $effect(() => {
    const ng = notesGrouped;
    const ss = songStructure;
    const sf = songFile;
    const cm = mode;
    const cd = contentDiv;

    if (ng && ss && sf && cm === "source" && cd) {
      // Use setTimeout to break out of effect context
      setTimeout(() => buildStaticDecorations(), 0);
    }
  });

  function buildStaticDecorations() {
    buildStaticDecorationsUtil({
      contentDiv,
      songFile,
      notesGrouped,
      instruments: songStructure?.instruments,
      highlighter,
      noteElements,
    });
  }

  function handleGenerating(generating) {
    isGenerating = generating;
  }

  function handleSeek(event) {
    if (audioPlayer) {
      audioPlayer.play(event.detail.time);
    }
  }
</script>

{#if loading}
  <div class="flex items-center justify-center p-4 w-full h-full">
    <LoaderCircle class="animate-spin" />
  </div>
{:else if file || songFile}
  <div class="flex flex-col gap-2 {mode === 'tiny' ? '' : 'h-full'}">
    <AudioPlayer
      bind:this={audioPlayer}
      {songFile}
      {songStructure}
      {isGenerating}
      onGenerating={handleGenerating}
      {highlighter}
      {noteElements}
      onCurrentTimeChange={(t) => (currentTime = t)}
      onPlayingChange={(p) => (playing = p)}
    />

    {#if mode === "source"}
      <div
        bind:this={contentDiv}
        class="flex-1 min-h-0 overflow-auto font-mono whitespace-pre-wrap p-4 bg-background"
      >
        {songFile}
      </div>
    {:else if mode === "visual" && songStructure}
      <div class="flex-1 min-h-0 h-full w-full">
        <Structure
          {songStructure}
          {instrumentColors}
          {currentTime}
          {playing}
          on:seek={handleSeek}
        />
      </div>
    {/if}
  </div>
{:else}
  <div class="text-muted-foreground p-4">Failed to load file</div>
{/if}
